import React from 'react';
import { Button, Spinner } from 'react-bootstrap';
import NotFound from '../../NotFound';
import TaskSearchItem from '../TaskSearchItem';
import { TASK_GROUP_CONFIG, TASK_GROUP_KEYS, TASK_PAST_GROUP_KEY } from '../../../helpers/constants';
import Accordion from '../../common/Accordion';

const LoadMoreButton = ({ loading, onClick }) => (
  <div className="text-right my-3">
    <Button variant="link" size="sm" className="text-muted shadow-none" disabled={loading} onClick={onClick}>
      {loading ? (
        <>
          <Spinner animation="border" size="sm" className="mr-2" />
          Loading...
        </>
      ) : (
        'Load more'
      )}
    </Button>
  </div>
);

const TaskGroupSection = ({
  groupKey,
  tasks,
  onTaskDelete,
  onEditClick,
  onStatusUpdate,
  loadMore,
  highlightedTaskId,
  totalCount,
  showDescription = false
}) => {
  if (!tasks?.length) return null;

  const config = TASK_GROUP_CONFIG[groupKey];
  const Icon = config.icon;

  return (
    <Accordion
      title={
        <div className="d-flex align-items-center py-2" style={{ gap: 8 }}>
          <Icon size={14} color={config.iconColor} />
          <span className="smallFont font-weight-semibold" style={{ color: config.labelColor }}>
            {config.label}
          </span>
          <span
            className="xtinyFont font-weight-bold py-1 px-2 rounded-pill"
            style={{ background: config.countBg, color: config.countColor }}
          >
            {totalCount || tasks?.length}
          </span>
        </div>
      }
      defaultOpen={true}
      className="border-bottom"
    >
      <div className="mb-3">
        <div className="row">
          {tasks.map(task => (
            <div key={task?._id} className="col-12 col-md-6 mb-2 d-flex">
              <TaskSearchItem
                task={task}
                onTaskDelete={onTaskDelete}
                onEditClick={onEditClick}
                onStatusUpdate={onStatusUpdate}
                isHighlighted={highlightedTaskId === task._id}
                showDescription={showDescription}
              />
            </div>
          ))}
        </div>
        {loadMore}
      </div>
    </Accordion>
  );
};

const TasksGroupedListView = ({
  taskBuckets = {},
  laterHasMore,
  pastHasMore,
  laterLoadingMore,
  pastLoadingMore,
  onLoadMoreLater,
  onLoadMorePast,
  onTaskDelete,
  onEditClick,
  onStatusUpdate,
  statusTab,
  highlightedTaskId,
  laterTotal = 0,
  pastTotal = 0,
  showDescription = false
}) => {
  const totalCount =
    TASK_GROUP_KEYS.reduce((sum, key) => sum + (taskBuckets[key]?.length || 0), 0) +
    (taskBuckets[TASK_PAST_GROUP_KEY]?.length || 0);

  if (totalCount === 0) {
    return <NotFound text="No tasks found" />;
  }

  const columnKeys = statusTab !== 'Incomplete' ? [...TASK_GROUP_KEYS, TASK_PAST_GROUP_KEY] : TASK_GROUP_KEYS;

  const getLoadMore = groupKey => {
    if (groupKey === 'later' && laterHasMore) {
      return <LoadMoreButton loading={laterLoadingMore} onClick={onLoadMoreLater} />;
    }
    if (groupKey === 'pastTasks' && pastHasMore) {
      return <LoadMoreButton loading={pastLoadingMore} onClick={onLoadMorePast} />;
    }
    return null;
  };

  return (
    <div className="py-2">
      {columnKeys.map(groupKey => (
        <TaskGroupSection
          key={groupKey}
          groupKey={groupKey}
          tasks={taskBuckets[groupKey]}
          onTaskDelete={onTaskDelete}
          onEditClick={onEditClick}
          onStatusUpdate={onStatusUpdate}
          highlightedTaskId={highlightedTaskId}
          totalCount={groupKey === 'later' ? laterTotal : groupKey === 'pastTasks' ? pastTotal : undefined}
          loadMore={getLoadMore(groupKey)}
          showDescription={showDescription}
        />
      ))}
    </div>
  );
};

export default TasksGroupedListView;
